import React, { useState, useMemo } from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useResponsive } from '../../config/layoutConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSafeAreaBottom } from '../../hooks/useSafeArea';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HomeRounded as HomeIcon,
  HealthAndSafety as HealthInsightsIcon,
  Medication as CarePlanIcon,
  CalendarToday as CalendarIcon,
  Mail as MailIcon,
  AttachMoney as ExpensesIcon,
  Group as CareTeamIcon,
  MoreHoriz as MoreIcon,
  Security as AdminIcon,
  Close as CloseIcon,
  AutoAwesome as AskSuprieIcon
} from '@mui/icons-material';
import { useAdminAuth } from '../../hooks/useAdminAuth';

// Primary bottom nav items (5 max per Material Design / Apple HIG)
const NAV_ITEMS = [
  { label: 'Home', icon: <HomeIcon />, path: '/home', color: '#0EA5E9' },
  { label: 'Health', icon: <HealthInsightsIcon />, path: '/health', color: '#10B981' },
  { label: 'Care Plan', icon: <CarePlanIcon />, path: '/care-plan', color: '#06B6D4' },
  { label: 'Appointments', icon: <CalendarIcon />, path: '/calendar', color: '#EC4899' },
];

// "More" sheet items (base — admin item added dynamically)
const BASE_MORE_ITEMS = [
  { label: 'Pet Expenses', icon: <ExpensesIcon />, path: '/pet-expenses', color: '#3B82F6' },
  { label: 'Messages', icon: <MailIcon />, path: '/inbox', color: '#64748B' },
  { label: 'Care Team', icon: <CareTeamIcon />, path: '/care-team', color: '#8B5CF6' },
  { label: 'Ask Suprie', icon: <AskSuprieIcon />, path: '/ask-suprie', color: '#A855F7' },
];

const MobileBottomNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { shouldUseMobileLayout } = useResponsive();
  const [moreOpen, setMoreOpen] = useState(false);
  const { isAdmin } = useAdminAuth();

  const moreItems = useMemo(() => {
    const items = [...BASE_MORE_ITEMS];
    if (isAdmin) {
      items.push({ label: 'Admin', icon: <AdminIcon />, path: '/admin', color: '#F59E0B' });
    }
    return items;
  }, [isAdmin]);

  const getCurrentValue = () => {
    const currentPath = location.pathname;
    const idx = NAV_ITEMS.findIndex(
      (item) => item.path === currentPath || currentPath.startsWith(item.path + '/')
    );
    if (idx !== -1) return idx;
    // Check if current path matches a "More" item — highlight "More" tab (index 4)
    const inMore = moreItems.some(
      (item) => item.path === currentPath || currentPath.startsWith(item.path + '/')
    );
    return inMore ? 4 : 0;
  };

  const handleNavigation = (_: React.SyntheticEvent, value: number) => {
    if (value === 4) {
      setMoreOpen((prev) => !prev);
      return;
    }
    setMoreOpen(false);
    const target = NAV_ITEMS[value]?.path;
    if (target && location.pathname !== target) navigate(target);
  };

  const handleMoreItemClick = (path: string) => {
    setMoreOpen(false);
    if (location.pathname !== path) navigate(path);
  };

  if (!shouldUseMobileLayout) return null;

  return (
    <>
      {/* More bottom sheet */}
      <AnimatePresence>
        {moreOpen && (
          <>
            {/* Scrim */}
            <motion.div
              key="scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMoreOpen(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.4)',
                zIndex: 1200,
              }}
            />
            {/* Sheet */}
            <motion.div
              key="sheet"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1202,
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                backgroundColor: theme.palette.background.paper,
                boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
                paddingBottom: `calc(80px + env(safe-area-inset-bottom))`,
              }}
            >
              {/* Drag handle */}
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 1 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 4,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.text.primary, 0.2),
                  }}
                />
              </Box>

              <List sx={{ px: 1 }}>
                {moreItems.map((item) => {
                  const isActive =
                    location.pathname === item.path ||
                    location.pathname.startsWith(item.path + '/');
                  return (
                    <ListItemButton
                      key={item.path}
                      onClick={() => handleMoreItemClick(item.path)}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        py: 1.5,
                        bgcolor: isActive
                          ? alpha(theme.palette.primary.main, 0.08)
                          : 'transparent',
                      }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 44, color: isActive ? item.color : 'text.secondary' }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? 'text.primary' : 'text.secondary',
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bottom navigation bar */}
      <Paper
        elevation={12}
        data-testid="bottom-navigation"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1201,
          borderRadius: 0,
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.18)',
          borderTop: `1px solid ${theme.palette.divider}`,
          ...getSafeAreaBottom(),
        }}
      >
        <BottomNavigation
          showLabels
          value={getCurrentValue()}
          onChange={handleNavigation}
          sx={{
            paddingX: 0.5,
            paddingBottom: `calc(8px + env(safe-area-inset-bottom))`,
            paddingTop: '6px',
            minHeight: 64,
            height: 'auto',
            '& .MuiBottomNavigationAction-root': {
              minWidth: 0,
              padding: '4px 8px',
              gap: 0.25,
            },
            '& .MuiBottomNavigationAction-icon': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              minWidth: 40,
              height: 40,
              minHeight: 40,
            },
            '& .MuiBottomNavigationAction-icon .MuiSvgIcon-root': {
              fontSize: 35,
              display: 'block',
            },
            '& .Mui-selected': {
              color: theme.palette.primary.main,
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.65rem',
                opacity: 1,
              },
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.65rem',
              opacity: 0.8,
            },
          }}
        >
          {NAV_ITEMS.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={item.label}
              icon={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    '& .MuiSvgIcon-root': {
                      fontSize: 35,
                      display: 'block',
                      ...('iconAlignFix' in item && item.iconAlignFix
                        ? item.iconAlignFix
                        : {}),
                    },
                  }}
                >
                  {item.icon}
                </Box>
              }
              sx={{
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: item.color || theme.palette.primary.main,
                },
              }}
            />
          ))}
          {/* More tab */}
          <BottomNavigationAction
            label="More"
            icon={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  '& .MuiSvgIcon-root': { fontSize: 35, display: 'block' },
                }}
              >
                {moreOpen ? <CloseIcon /> : <MoreIcon />}
              </Box>
            }
            sx={{
              color: 'text.secondary',
              '&.Mui-selected': { color: theme.palette.primary.main },
            }}
          />
        </BottomNavigation>
      </Paper>
    </>
  );
};

export default MobileBottomNavigation;
